import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  combineLatest,
  concat,
  fromEvent,
  of,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  finalize,
  map,
  mergeMap,
  shareReplay,
  skip,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  AddOrUpdatePersistenceInput,
  GetWorkflowCountsGQL,
  LabelCount,
  WorkflowCounts,
  WorkflowType,
} from 'src/app/graphql/generated';
import {
  SubscriptionWorkflowCountsUpdateGQL,
  SubscriptionWorkflowType,
} from 'src/app/graphql/generated-subscription';
import { TextCode } from 'src/app/shared/models/text-code.interface';
import { PersistenceGraphqlApisService } from 'src/app/shared/services/graphql-apis/persistence-graphql-apis.service';
import { SubTenantsService } from 'src/app/shared/services/sub-tenants/sub-tenants.service';
import { UserService } from 'src/app/shared/services/user.service';

export const defaultSidebarPersistence: SidebarPersistence = {
  selectedOriginators: [],
  isSidebarExpanded: true,
  isFundingOpen: true,
  isCollateralOpen: true,
  isSettlementOpen: true,
};

export interface SidebarPersistence {
  /**
   * Sub-tenant ids selected in the sidebar originator filter.
   * For data fetching, use {@link LeftSidebarMenuService.selectedOriginators$} or
   * {@link LeftSidebarMenuService.getSelectedOriginators} — not
   * {@link LeftSidebarMenuService.sidebarPersistenceState$}, which also emits on
   * accordion and sidebar UI changes.
   */
  selectedOriginators: string[];
  isSidebarExpanded: boolean;
  isFundingOpen: boolean;
  isCollateralOpen: boolean;
  isSettlementOpen: boolean;
}

export function sortWorkflowCounts(counts: LabelCount[]): LabelCount[] {
  return counts.sort(
    (a, b) => a.description?.localeCompare(b.description ?? '') ?? 0
  );
}

@Injectable({
  providedIn: 'root',
})
export class LeftSidebarMenuService {
  private readonly sidebarPersistenceState: BehaviorSubject<SidebarPersistence> =
    new BehaviorSubject<SidebarPersistence>(defaultSidebarPersistence);

  private readonly browserTabVisible$: Observable<boolean> = fromEvent(
    document,
    'visibilitychange'
  ).pipe(
    startWith(null),
    map(() => !document.hidden),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly sidebarRouteArea$ = new BehaviorSubject<{
    isOnAdminRoute: boolean;
    isOnFinancingRoute: boolean;
  }>({
    isOnAdminRoute: false,
    isOnFinancingRoute: false,
  });

  public sidebarPersistenceState$ = this.sidebarPersistenceState.asObservable();

  /**
   * Emits when the sidebar originator (sub-tenant) filter changes.
   * Use for data fetching — not {@link sidebarPersistenceState$}, which also emits on
   * accordion expand/collapse and other UI persistence updates.
   */
  public readonly selectedOriginators$ = this.sidebarPersistenceState$.pipe(
    map((state) => state.selectedOriginators),
    distinctUntilChanged((a, b) => this.sameSubTenantIds(a, b)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly workflowSubTenantSelectionHydrated$ =
    new BehaviorSubject<boolean>(false);

  public readonly panelLoaders = {
    [WorkflowType.FUNDING]: new BehaviorSubject<boolean>(false),
    [WorkflowType.COLLATERAL]: new BehaviorSubject<boolean>(false),
    [WorkflowType.SETTLEMENT]: new BehaviorSubject<boolean>(false),
  };

  private readonly sidebarWorkflowCountsActive$: Observable<boolean> =
    combineLatest([
      this.sidebarPersistenceState$.pipe(
        map((state) => state.isSidebarExpanded),
        distinctUntilChanged()
      ),
      this.sidebarRouteArea$,
    ]).pipe(
      distinctUntilChanged(
        (a, b) =>
          a[0] === b[0] &&
          a[1].isOnAdminRoute === b[1].isOnAdminRoute &&
          a[1].isOnFinancingRoute === b[1].isOnFinancingRoute
      ),
      map(
        ([expanded, route]) =>
          expanded && !route.isOnAdminRoute && !route.isOnFinancingRoute
      )
    );

  /**
   * Filters the source observable based on the browser tab visibility.
   * If the browser tab is not visible, the source observable is filtered to an empty array.
   * This is to prevent the sidebar from loading counts when the tab is not visible to prevent unnecessary CD cycles.
   * @param source$ the source observable to filter
   * @returns
   */
  private whenTabVisible$<T>(source$: Observable<T>): Observable<T> {
    return combineLatest([source$, this.browserTabVisible$]).pipe(
      map(([value, tabVisible]) => (tabVisible ? value : ([] as unknown as T)))
    );
  }

  /**
   * gets all subtenants sorted by originator name alphabetically from the sub-tenants service
   */
  public allSubTenants$: Observable<TextCode[]> =
    this.originatorsService.getSubTenants();

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly userService: UserService,
    private readonly persistenceService: PersistenceGraphqlApisService,
    private readonly originatorsService: SubTenantsService,
    private readonly getWorkflowCountsGQL: GetWorkflowCountsGQL,
    private readonly workflowCountsUpdateSubscription: SubscriptionWorkflowCountsUpdateGQL
  ) {
    /**
     * Updates the sidebar persistence when the sidebar accordion state, selected subtenant ids, or sidebar expanded state change
     */
    this.sidebarPersistenceState$
      .pipe(skip(1), debounceTime(1000), takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this.updateSidebarPersistence(state);
      });
  }

  private getWorkflowCounts$(
    workflowType: WorkflowType | SubscriptionWorkflowType
  ): Observable<LabelCount[]> {
    return combineLatest([
      this.sidebarWorkflowCountsActive$,
      this.selectedOriginators$,
      this.workflowSubTenantSelectionHydrated$,
    ]).pipe(
      tap(([isWorkflowActive, _, __]) =>
        isWorkflowActive
          ? this.panelLoaders[workflowType as WorkflowType].next(true)
          : null
      ),
      distinctUntilChanged(
        ([activeA, idsA, hydratedA], [activeB, idsB, hydratedB]) =>
          activeA === activeB &&
          this.sameSubTenantIds(idsA, idsB) &&
          hydratedA === hydratedB
      ),
      switchMap(([active, originatorIds, hydrated]) => {
        if (!hydrated) {
          return of([] as LabelCount[]);
        }

        if (!active) {
          return EMPTY;
        }

        const emptyWorkflowCountsRow: WorkflowCounts = {
          workflowType: workflowType as WorkflowType,
          labelCounts: [],
        };

        const initialCounts$ = this.getWorkflowCountsGQL
          .fetch({
            variables: {
              input: {
                subTenantIds: originatorIds,
                workflowType: workflowType as WorkflowType,
              },
            },
          })
          .pipe(
            mergeMap(({ data }) => {
              const row = data?.workflowCounts.find(
                (c) => c.workflowType === workflowType
              );

              return of((row ?? emptyWorkflowCountsRow) as WorkflowCounts);
            }),
            finalize(() =>
              this.panelLoaders[workflowType as WorkflowType].next(false)
            ),
            catchError(() => of(emptyWorkflowCountsRow as WorkflowCounts))
          );

        const workflowTypeKey = workflowType as WorkflowType;

        const liveCounts$ = this.workflowCountsUpdateSubscription
          .subscribe({
            variables: {
              input: {
                subTenantIds: originatorIds,
                workflowType: workflowType as SubscriptionWorkflowType,
              },
            },
          })
          .pipe(
            mergeMap(({ data }) => {
              const row = data?.workflowCountsUpdate?.find(
                (c) =>
                  c.workflowType === (workflowType as SubscriptionWorkflowType)
              );

              return of(
                (row ?? emptyWorkflowCountsRow) as unknown as WorkflowCounts
              );
            }),
            switchMap((countsRow) =>
              of(countsRow).pipe(
                tap(() => this.panelLoaders[workflowTypeKey].next(true)),
                delay(2000),
                finalize(() => this.panelLoaders[workflowTypeKey].next(false))
              )
            ),
            tap({
              error: () => this.panelLoaders[workflowTypeKey].next(false),
            })
          );

        return concat(initialCounts$, liveCounts$).pipe(
          map((counts) => {
            const filteredCounts = (counts.labelCounts ?? []).filter(
              (c): c is LabelCount => c != null && c.count !== 0
            );

            return sortWorkflowCounts(filteredCounts);
          })
        );
      })
    );
  }

  private sortFundingWorkflowCounts(counts: LabelCount[]): LabelCount[] {
    const order = [
      'New',
      'Pending Approval',
      'Approved',
      'Processing',
      'Returned',
    ];

    return counts
      .filter(
        (c) => order.includes(c.description ?? '') || c.label === 'funding'
      )
      .sort((a, b) => {
        const aIndex = order.indexOf(a.description ?? '');
        const bIndex = order.indexOf(b.description ?? '');
        return aIndex - bIndex;
      });
  }

  public getFundingCounts$(): Observable<LabelCount[]> {
    return this.whenTabVisible$(
      this.getWorkflowCounts$(WorkflowType.FUNDING).pipe(
        map((counts) => this.sortFundingWorkflowCounts(counts))
      )
    );
  }

  public getSettlementCounts$(): Observable<LabelCount[]> {
    return this.whenTabVisible$(
      this.getWorkflowCounts$(WorkflowType.SETTLEMENT)
    );
  }

  public getCollateralCounts$(): Observable<LabelCount[]> {
    return this.whenTabVisible$(
      this.getWorkflowCounts$(WorkflowType.COLLATERAL)
    );
  }

  public getIsOnAdminRoute$(): Observable<boolean> {
    return this.sidebarRouteArea$.pipe(
      map((route) => route.isOnAdminRoute),
      distinctUntilChanged()
    );
  }

  public getIsOnFinancingRoute$(): Observable<boolean> {
    return this.sidebarRouteArea$.pipe(
      map((route) => route.isOnFinancingRoute),
      distinctUntilChanged()
    );
  }

  public setSidebarRouteAreas(
    isOnAdminRoute: boolean,
    isOnFinancingRoute: boolean
  ): void {
    const { isOnAdminRoute: isAdmin, isOnFinancingRoute: isFinancing } =
      this.sidebarRouteArea$.value;

    if (isAdmin === isOnAdminRoute && isFinancing === isOnFinancingRoute) {
      return;
    }
    this.sidebarRouteArea$.next({ isOnAdminRoute, isOnFinancingRoute });
  }

  public toggleSidebar(): void {
    this.updateSidebarPersistenceState({
      isSidebarExpanded: !this.sidebarPersistenceState.value.isSidebarExpanded,
    });
  }

  sameSubTenantIds(a: string[], b: string[]): boolean {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    return a.every((id, i) => id === b[i]);
  }

  public getSidebarStateFromPersistence$(): Observable<SidebarPersistence> {
    return this.persistenceService
      .getPersistenceSettings({
        origin: 'UserId',
        originId: this.userService.claims.userId || '',
        originType: 'Sidebar:Workflows',
      })
      .pipe(
        map(({ data: { persistence } }) => {
          if (persistence?.length) {
            const persistenceState = JSON.parse(
              persistence[0]?.persistenceJson
            );

            this.updateSidebarPersistenceState({
              isSidebarExpanded: persistenceState?.isSidebarExpanded || false,
              isFundingOpen: persistenceState?.isFundingOpen || false,
              isCollateralOpen: persistenceState?.isCollateralOpen || false,
              isSettlementOpen: persistenceState?.isSettlementOpen || false,
            });

            return persistenceState;
          }
          return [];
        })
      );
  }

  public notifyWorkflowSubTenantSelectionHydrated(): void {
    this.workflowSubTenantSelectionHydrated$.next(true);
  }

  public getSidebarPersistenceState(): SidebarPersistence {
    return this.sidebarPersistenceState.value;
  }

  /**
   * Returns the current sidebar originator (sub-tenant) filter ids.
   * Prefer {@link selectedOriginators$} for reactive data fetching.
   */
  public getSelectedOriginators(): string[] {
    return this.sidebarPersistenceState.value.selectedOriginators;
  }

  public updateSidebarPersistenceState(
    persistence: Partial<SidebarPersistence>
  ) {
    this.sidebarPersistenceState.next({
      ...this.sidebarPersistenceState.value,
      ...persistence,
    });
  }

  private updateSidebarPersistence(persistence: SidebarPersistence) {
    const input: AddOrUpdatePersistenceInput = {
      origin: 'UserId',
      originId: this.userService.claims.userId,
      originType: 'Sidebar:Workflows',
      persistenceJson: JSON.stringify(persistence),
    };
    this.persistenceService.addOrUpdatePersistence(input).subscribe();
  }
}
